import { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDropzone } from 'react-dropzone';

import { ButtonPrimary, Icon, ModalClose } from '../../common/components';
import { FormProvider, useForm } from 'react-hook-form';
import ReactSelect from 'react-select';
import { FAQManagementModalWrapper } from './FAQManagementStyles.js';
import * as colors from '../../common/styles/colors';
import FormElement from '../../common/components/FormElement';
import React from 'react';
import { iconsToUse } from '../../common/styles/themes';

type FAQManagementModalProps = {
  show: boolean;
  edit: boolean;
  item: Api.IFAQRequest;
  handleDownloadGenericDocument: Function;
  onSubmit: (isAccepted: boolean, item: any) => void;
  onClose: () => void;
};

const defaultProps = {
  show: true,
  edit: false,
  item: {},
  onSubmit: (isAccepted: boolean, item: any) => console.log(isAccepted),
};

type Inputs = {
  email: string;
  downloadReport: { label: string; value: string };
};

interface IOption {
  value: string;
  label: string;
}

interface IOption2 {
  value: number;
  label: string;
}

export const FAQManagementModal = (props: FAQManagementModalProps) => {
  const { show, onSubmit } = props;
  const [submitForm, setSubmitForm] = useState(false);

  const methods = useForm<Inputs>({ defaultValues: {} });

  const [files, setFiles] = useState<Array<File>>([]);
  const [question, setQuestion] = useState<string>('');
  const [answer, setAnswer] = useState<string>('');
  const [supportingLink, setSupportingLink] = useState<string>('');
  const [imageSupportingText, setImageSupportingText] = useState<string>('');

  const [audienceOption, setAudienceOption] = useState<IOption2>();
  const [categoryOption, setCategoryOption] = useState<IOption2>();
  const [statusElement, setStatusElement] = useState<number>(1);
  const [priorityOption, setPriorityOption] = useState<IOption>();

  const [storageKey, setStorageKey] = useState<string | undefined>(props.item.storageKey);

  const [showUploadTooBigError, setShowUploadTooBigError] = useState<boolean>(false);
  const [showUploadMustBeImageError, setShowUploadMustBeImageError] = useState<boolean>(false);
  const [showUploadTooManyFilesError, setShowUploadTooManyFilesError] = useState<boolean>(false);

  const [updateModalFileArray, setUpdateModalFileArray] = useState<Array<string>>([]);

  const dropboxDisabled = updateModalFileArray.length > 0 || files.length > 0;

  const audienceOptions = [
    {
      value: 1,
      label: 'All',
    },
    {
      value: 2,
      label: 'Customer',
    },
    {
      value: 3,
      label: 'TPI',
    },
    {
      value: 4,
      label: 'Internal',
    },
  ];

  const priorityOptions = [
    {
      value: 'High',
      label: 'High',
    },
    {
      value: 'Medium',
      label: 'Medium',
    },
    {
      value: 'Low',
      label: 'Low',
    },
  ];

  const categoryOptions = [
    {
      value: 1,
      label: 'Home page',
      audience: 'All',
    },
    {
      value: 2,
      label: 'Sustainability hub',
      audience: 'All',
    },
    {
      value: 3,
      label: 'My messages',
      audience: 'All',
    },
    {
      value: 4,
      label: 'My documents',
      audience: 'All',
    },
    {
      value: 5,
      label: 'Account details',
      audience: 'All',
    },
    {
      value: 6,
      label: 'MPANs',
      audience: 'All',
    },
    {
      value: 7,
      label: 'My invoices',
      audience: 'All',
    },
    {
      value: 8,
      label: 'Energy usage',
      audience: 'All',
    },
    {
      value: 9,
      label: 'Scheduled reporting',
      audience: 'All',
    },
    {
      value: 10,
      label: 'Exclusive offers',
      audience: 'All',
    },
    {
      value: 11,
      label: 'Our insights',
      audience: 'All',
    },
    {
      value: 12,
      label: 'Select Customers',
      audience: 'TPI',
    },
    {
      value: 13,
      label: 'Broker/TPI data',
      audience: 'TPI',
    },
    {
      value: 14,
      label: 'Troubleshooting',
      audience: 'Internal',
    },
    {
      value: 15,
      label: 'Payments',
      audience: 'Customer',
    },
  ];

  const [categoryOptionsFiltered, setCategoryOptionsFiltered] = useState<Array<any>>();

  const statusElements = ['', 'Draft', 'Ready to publish', 'Published'];

  const totalSizeLimit = 20 * 1024 * 1024; // 20 MB

  const getTotalPreviousUploadedFilesSize: any = () => {
    var totalSize = 0;
    files.forEach((file: File) => {
      totalSize = totalSize + file.size;
    });
    return totalSize;
  };

  const getFileExt = (fileName: string) => {
    if (!fileName) return 'Excel';

    const fileExt = fileName.split('.').pop();

    if (fileExt === 'png') {
      return 'png';
    } else {
      return 'jpg';
    }
  };

  const formatSize = (size: number) => {
    const fSExt = new Array('Bytes', 'KB', 'MB', 'GB');
    var i = 0;
    while (size > 900) {
      size /= 1024;
      i++;
    }
    var exactSize = Math.round(size * 100) / 100 + ' ' + fSExt[i];
    return exactSize;
  };

  const findExistingFile: any = (droppedFiles: Array<File>) => {
    var existingFiles: Array<File> = [];

    droppedFiles.forEach((file: File) => {
      const index = files.findIndex(f => f.name === file.name);
      if (index >= 0) {
        existingFiles.push(file);
      }
    });
    return existingFiles;
  };

  const onDrop = (droppedFiles: Array<File>) => {
    setShowUploadTooBigError(false);
    setShowUploadMustBeImageError(false);
    setShowUploadTooManyFilesError(false);

    const existingFilesCount = files.length;
    const droppedFilesCount = droppedFiles.length;
    if (existingFilesCount + droppedFilesCount > 1) {
      setShowUploadTooManyFilesError(true);
      return;
    }

    let alreadyUploadedTotalSize = getTotalPreviousUploadedFilesSize();
    droppedFiles.forEach((file: File) => {
      alreadyUploadedTotalSize += file.size;
    });
    if (alreadyUploadedTotalSize > totalSizeLimit) {
      setShowUploadTooBigError(true);
      return;
    }

    // Check for duplicates
    const existingFiles = findExistingFile(droppedFiles);
    if (existingFiles) {
      deleteFiles(existingFiles);
    }

    setFiles([...files, ...droppedFiles]);
  };

  const { fileRejections, getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    multiple: false,
    disabled: dropboxDisabled,
  });

  const renderedFiles = files.map((file: any, index: number) => (
    <React.Fragment key={index}>
      <div className='col-12 mt-2'>
        <div className='file'>
          <div className='pl-2'>
            <div className='filepath'>{file.path}</div>
            <button className='close' onClick={event => handleDelete(event, file)}>
              <span aria-hidden='true'>
                <Icon color={colors.darkGrey} size={16} icon='cross' />
              </span>
            </button>
          </div>
          <div className='pl-2'>
            <div className='filesize'>{formatSize(file.size)}</div>
          </div>
        </div>
      </div>
    </React.Fragment>
  ));

  const renderedFilesForUpdate = updateModalFileArray.map((name: any, index: number) => (
    <React.Fragment key={index}>
      <div className='col-6 mt-2'>
        <div className='file'>
          <div className='pl-2'>
            <div className='filepath' onClick={() => handleDownloadGenericDocument(props.item.faqDocumentId)}>
              {name}
            </div>
            <button className='closeUpdate' onClick={() => handleRemoveFile()}>
              <span aria-hidden='true'>
                <Icon color={colors.darkGrey} size={16} icon='cross' />
              </span>
            </button>
          </div>
        </div>
      </div>
    </React.Fragment>
  ));

  useEffect(() => {
    let fileNameArray: Array<string> = [];

    if (props.show && props.edit) {
      if (props.item.fileName) {
        fileNameArray.push(props.item.fileName);
        setUpdateModalFileArray(fileNameArray);
      }
    }
  }, [props.show]);

  useEffect(() => {
    const rejections: any = fileRejections;
    if (rejections) {
      rejections.map((file: any) => {
        if (file && file.errors.length > 0) {
          file.errors.map((error: any) => {
            if (error.code === 'too-many-files') {
              setShowUploadTooManyFilesError(true);
              return;
            } else if (error.code === 'file-invalid-type') {
              setShowUploadMustBeImageError(true);
              return;
            }
          });
        }
      });
    }

    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileRejections]);

  useEffect(() => {
    setQuestion(props.item.question ? props.item.question : '');
    setAnswer(props.item.answer ? props.item.answer : '');
    setSupportingLink(props.item.supportingLink ? props.item.supportingLink : '');
    setImageSupportingText(props.item.imageSupportingText ? props.item.imageSupportingText : '');

    if (props.item.faqAudienceId) {
      const selectedAudience = audienceOptions.find(item => item.value === props.item.faqAudienceId);
      setAudienceOption(selectedAudience);

      if (selectedAudience) {
        filterCategories(selectedAudience.label);
      }
    } else {
      setAudienceOption(undefined);
    }

    if (props.item.faqCategoryId) {
      const selectedCategory = categoryOptions.find(item => item.value === props.item.faqCategoryId);
      setCategoryOption(selectedCategory);
    } else {
      setCategoryOption(undefined);
    }

    if (props.item.statusId) {
      setStatusElement(props.item.statusId);
    } else {
      setStatusElement(1);
    }

    if (props.item.priority) {
      const selectedPriority = priorityOptions.find(
        item => item.value?.toLowerCase() === props.item.priority?.toLowerCase()
      );

      setPriorityOption(selectedPriority);
    } else {
      setPriorityOption(undefined);
    }

    //ADD FILE HERE
    if (props.item.file) {
      setFiles([props.item.file]);
    } else {
      setFiles([]);
    }
  }, [props.item, props.show]);

  useEffect(() => {
    var valid = false;

    const validCategoryOption = categoryOption != null && categoryOption.value != -1;

    valid =
      question !== '' &&
      answer !== '' &&
      audienceOption != null &&
      validCategoryOption &&
      priorityOption != null &&
      statusElement >= 0;

    setSubmitForm(valid);
  }, [question, answer, audienceOption, categoryOption, priorityOption, statusElement]);

  const filterCategories = (aud: string) => {
    let filteredOptions: Array<any> = [];

    if (aud === 'All') {
      filteredOptions = categoryOptions.filter(c => c.audience === 'All');
    } else if (aud === 'Customer') {
      filteredOptions = categoryOptions.filter(c => c.audience === 'Customer');
    } else if (aud === 'TPI') {
      filteredOptions = categoryOptions.filter(c => c.audience === 'TPI');
    } else if (aud === 'Internal') {
      filteredOptions = categoryOptions.filter(c => c.audience === 'Internal');
    }

    setCategoryOptionsFiltered(filteredOptions);
  };

  const handleAudienceChange = async (audience: any) => {
    const option: IOption2 = {
      value: -1,
      label: '',
    };
    setCategoryOption(option);

    setAudienceOption(audience);
    filterCategories(audience.label);
  };

  const handleCategoryChange = async (category: any) => {
    setCategoryOption(category);
  };

  const handlePriorityChange = async (priority: any) => {
    setPriorityOption(priority);
  };

  const handleDownloadGenericDocument = async (id: any) => {
    props.handleDownloadGenericDocument(id);
  };

  const handleRemoveFile = async () => {
    setUpdateModalFileArray([]);
    setStorageKey(undefined);
  };

  const deleteFiles = (filesToDelete: Array<File>) => {
    filesToDelete.forEach((file: File) => {
      const tmp = files;
      const index = tmp.findIndex(f => f.name === file.name);
      if (index >= 0) {
        tmp.splice(index, 1);
        setFiles([...tmp]);
      }
    });
  };

  const handleDelete = (event: any, file: File) => {
    event.preventDefault();
    deleteFiles([file]);
  };

  return (
    <>
      <Modal animation={false} className={'custom-modal'} show={show} size={'xl'} backdrop='static' centered>
        <FAQManagementModalWrapper>
          <Modal.Header>
            {props.edit ? <Modal.Title>Update FAQ</Modal.Title> : <Modal.Title>New FAQ</Modal.Title>}
            <ModalClose onClick={props.onClose} />
          </Modal.Header>

          <Modal.Body>
            <FormProvider {...methods}>
              <form>
                <div className='d-flex-column'>
                  <div className='my-2 audience'>
                    <div className=''>Select who can view the FAQ *</div>
                    <ReactSelect
                      autoFocus={false}
                      blurInputOnSelect={true}
                      placeholder={''}
                      value={audienceOption}
                      options={audienceOptions}
                      onChange={handleAudienceChange}
                    />
                  </div>

                  <div className='container-fluid'>
                    <div className='row justify-content-left'>
                      <div className='category'>
                        <div className='label'>Category *</div>
                        <ReactSelect
                          autoFocus={false}
                          blurInputOnSelect={true}
                          placeholder={''}
                          value={categoryOption}
                          options={categoryOptionsFiltered}
                          onChange={handleCategoryChange}
                        />
                      </div>

                      <div className='pl-4 priority'>
                        <div className='label'>Priority *</div>
                        <ReactSelect
                          autoFocus={false}
                          blurInputOnSelect={true}
                          placeholder={''}
                          value={priorityOption}
                          options={priorityOptions}
                          onChange={handlePriorityChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className='pt-1'>
                    <div className=''>Question *</div>
                    <FormElement.Textarea
                      id='question'
                      placeholder={''}
                      // placeholder={'Max characters 150'}
                      className='c-form__input no-resize'
                      name='question'
                      value={question}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        setQuestion(e.target.value);
                      }}
                      rows={2}
                      // maxLength={150}
                      required={true}
                    />
                  </div>

                  <div className='pt-1'>
                    <div className=''>Answer *</div>
                    <FormElement.Textarea
                      id='answer'
                      placeholder={''}
                      // placeholder={'Max characters 150'}
                      className='c-form__input no-resize'
                      name='answer'
                      value={answer}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        setAnswer(e.target.value);
                      }}
                      rows={2}
                      // maxLength={150}
                      required={true}
                    />
                  </div>

                  <div className='pt-1'>
                    <div className=''>Supporting link</div>
                    <FormElement.Textarea
                      id='supportingLink'
                      placeholder={'Max characters 500'}
                      className='c-form__input no-resize'
                      name='supportingLink'
                      value={supportingLink}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        setSupportingLink(e.target.value);
                      }}
                      rows={1}
                      maxLength={500}
                      required={false}
                    />
                  </div>

                  <div className='container-fluid'>
                    <div className='row pt-1 pb-2'>
                      <div className='label'>Select file to upload</div>
                    </div>
                    <div className='row'>
                      <div className='pt-1 w-50'>
                        <div className=''>
                          <section className={`d-flex dropzonecontainer ${dropboxDisabled ? 'disabled' : ''}`}>
                            <div {...getRootProps({ className: 'dropzone d-flex' })}>
                              <input {...getInputProps()} />
                              <div className='loaIcon mr-5 my-auto'>
                                <Icon icon={iconsToUse.loaHealth} size={80} />
                              </div>

                              <div className='d-flex flex-column w-100 my-auto'>
                                <div className='pt-4 title'>Click to add or drag .png or JPEG image</div>
                                <div className='pb-4' style={{ fontStyle: 'italic' }}>
                                  maximum 1 file - 20mb total
                                </div>
                              </div>
                            </div>
                          </section>
                        </div>

                        <div className='errors'>
                          {showUploadTooBigError && <div>You can only upload files to a maximum of 20MB</div>}
                          {showUploadMustBeImageError && <div>You can only upload png or JPEG files</div>}
                          {showUploadTooManyFilesError && <div>You can only upload a maximum of 1 file</div>}
                        </div>
                      </div>

                      <div className='w-50'>
                        <div className='filecontainer'>
                          <div>{renderedFiles}</div>
                          <div>{renderedFilesForUpdate}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className=''>
                    <div className=''>Image supporting text</div>
                    <FormElement.Textarea
                      id='imageSupportingText'
                      placeholder={''}
                      className='c-form__input no-resize'
                      name='imageSupportingText'
                      value={imageSupportingText}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        setImageSupportingText(e.target.value);
                      }}
                      rows={1}
                      required={false}
                      disabled={renderedFiles.length == 0 && renderedFilesForUpdate.length == 0}
                    />
                  </div>

                  <div className='pt-2'>Status</div>
                  <FormElement.Radio
                    id='page-status'
                    className='c-form__input pt-0'
                    label={''}
                    name='page-status'
                    elements={['Ready to publish', 'Draft']}
                    value={statusElements[statusElement]}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const selectedStatusId = statusElements.findIndex(s => s == e.target.value);
                      setStatusElement(selectedStatusId);
                    }}
                  />
                </div>
              </form>
            </FormProvider>
          </Modal.Body>
          <div className='float-right'>
            <ButtonPrimary
              disabled={!submitForm}
              onClick={event => {
                event.preventDefault();

                const item: Api.IFAQRequest = {
                  storageKey: storageKey,
                  file: files[0],
                  format: files.length > 0 ? getFileExt(files[0].name) : getFileExt(updateModalFileArray[0]),
                  faqAudienceId: audienceOption ? audienceOption.value : -1,
                  faqCategoryId: categoryOption ? categoryOption.value : -1,
                  priority: priorityOption ? priorityOption.label : '',
                  question: question,
                  answer: answer,
                  supportingLink: supportingLink,
                  imageSupportingText: imageSupportingText,
                  statusId: statusElement,
                  internalOnly: false,
                };

                onSubmit(submitForm, item);
              }}
              className='mt-2 pt-2 upload-btn'
              title='Save'
            ></ButtonPrimary>

            <ButtonPrimary
              onClick={event => {
                event.preventDefault();
                props.onClose();
              }}
              className='ml-4 upload-btn'
              title='Cancel'
            ></ButtonPrimary>
          </div>
        </FAQManagementModalWrapper>
      </Modal>
    </>
  );
};

FAQManagementModal.defaultProps = defaultProps;
